<template>
  <div data-testid="whatsapp-template-modal">
    <modal
      elem-id="modal_whatsapp_message_templates"
      :show="isVisible"
      :title="modalTitle"
      size="medium"
      :close-label="t('Cancelar')"
      :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
      :save-fn="isDeleteMode ? handleDelete : handleSave"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Delete Confirmation Mode -->
        <div v-if="isDeleteMode" class="delete-confirmation">
          <p>{{ t('Tem certeza que deseja deletar este template?') }}</p>
          <p class="text-muted">{{ templateToDelete?.template_name }}</p>
        </div>

        <!-- Create/Edit Mode -->
        <div v-else>
          <WhatsappMessageTemplatesForm
            ref="formRef"
            :templateId="currentTemplateId ?? undefined"
            :isEditMode="isEditMode"
            @submit-success="handleFormSuccess"
            @submit-error="handleFormError"
          />
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWhatsappMessageTemplates } from '../../composables/useWhatsappMessageTemplates';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';
import WhatsappMessageTemplatesForm from './WhatsappMessageTemplatesForm.vue';

interface TemplateRow {
  template_id?: number | string;
  template_name?: string;
  [key: string]: unknown;
}

interface FormExposed {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  id?: number | string;
  mode?: 'create' | 'edit' | 'delete';
  data?: TemplateRow | null;
}

// Get i18n composable
const { t } = useI18n();

// Use composables
const { deleteTemplate } = useWhatsappMessageTemplates();

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentTemplateId = ref<number | string | null>(null);
const templateToDelete = ref<TemplateRow | null>(null);
const formRef = ref<FormExposed | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// Computed properties
const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Deletar Template');
  } else if (isEditMode.value) {
    return t('Editar Template');
  } else {
    return t('Novo Template');
  }
});

// Methods
const openForCreate = async (): Promise<void> => {
  log('WhatsappMessageTemplatesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentTemplateId.value = null;

  await nextTick();
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const openForEdit = async (templateId: number | string): Promise<void> => {
  log('WhatsappMessageTemplatesModal', 'openForEdit', 'Opening modal for edit', templateId);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentTemplateId.value = templateId;

  await nextTick();
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (
  templateId: number | string,
  templateData: TemplateRow | null = null
): Promise<void> => {
  log('WhatsappMessageTemplatesModal', 'openForDelete', 'Opening modal for delete', templateId);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentTemplateId.value = templateId;
  templateToDelete.value = templateData;
};

const handleClose = (): void => {
  log('WhatsappMessageTemplatesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentTemplateId.value = null;
  templateToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('WhatsappMessageTemplatesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentTemplateId.value) {
    logWarn('WhatsappMessageTemplatesModal', 'handleDelete', 'No template ID to delete');
    return;
  }

  log('WhatsappMessageTemplatesModal', 'handleDelete', 'Deleting template', currentTemplateId.value);
  isDeleting.value = true;

  try {
    await deleteTemplate(currentTemplateId.value);
    eventBus.emit('globalMessage.success', t('Template deletado com sucesso'));
    handleClose();
  } catch (error) {
    const err = error as Error;
    logError('WhatsappMessageTemplatesModal', 'handleDelete', 'Error deleting template', err);
    eventBus.emit('globalMessage.error', t('Erro ao deletar template') + ': ' + err.message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('WhatsappMessageTemplatesModal', 'handleFormSuccess', 'Form submitted successfully');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('WhatsappMessageTemplatesModal', 'handleFormError', 'Form submission error', error);
};

// Standard modal open handler (for modal.whatsappMessageTemplates.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('WhatsappMessageTemplatesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.mode === 'delete' && data?.id) {
    void openForDelete(data.id, data.data || null);
  } else if (data?.id || data?.mode === 'edit') {
    void openForEdit(data!.id as number | string);
  } else {
    void openForCreate();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('WhatsappMessageTemplatesModal', 'onMounted', 'Component mounted, setting up event listeners');

  eventBus.on('modal.whatsappMessageTemplates.open', handleModalOpen);
  eventBus.on('modal.whatsappMessageTemplates.close', handleClose);
});

onBeforeUnmount(() => {
  log('WhatsappMessageTemplatesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  eventBus.off('modal.whatsappMessageTemplates.open', handleModalOpen);
  eventBus.off('modal.whatsappMessageTemplates.close', handleClose);
});

// Expose methods for parent components (legacy support)
defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
